import React, { useState } from "react";

/* ---------------- Icons ---------------- */
const IconDoc = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
);
const IconWarn = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);
const IconBulb = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2.05V17h6v-.25c0-.85.4-1.55 1-2.05A7 7 0 0 0 12 2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);
const IconKey = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="8" cy="15" r="4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M11 12l9-9m0 0v4m0-4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IconShield = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M12 2l8 3v6c0 5-3.4 8.5-8 11-4.6-2.5-8-6-8-11V5l8-3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);
const IconClock = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);
const IconCheck = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const IconSpark = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

/* ---------------- Small components ---------------- */
function CopyBox({ url }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        try { await navigator.clipboard.writeText(url); } catch (e) { }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <div className="xf__url">
            <span className="xf__url-icon"><IconDoc /></span>
            <code className="xf__url-text">{url}</code>
            <button type="button" className={`xf__copy${copied ? " xf__copy--done" : ""}`} onClick={handleCopy}>
                {copied ? "Copied ✓" : "Copy"}
            </button>
        </div>
    );
}

function Shot({ caption, tone = "default", img = "" }) {
    return (
        <figure className={`xf__shot xf__shot--${tone}`}>
            <div className="xf__shot-frame">
                {img ? (
                    <img className="xf__shot-img" src={img} alt={caption || "Screenshot"} loading="lazy" />
                ) : (
                    <>
                        <span className="xf__shot-glow" />
                        <IconDoc />
                        <span>Screenshot preview</span>
                    </>
                )}
            </div>
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
}

function Note({ type = "tip", children }) {
    return (
        <div className={`xf__note xf__note--${type}`}>
            <span className="xf__note-icon">{type === "warn" ? <IconWarn /> : <IconBulb />}</span>
            <p>{children}</p>
        </div>
    );
}

function FieldRow({ label, value, tag, tagType, copyable = false }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            console.error("Failed to copy:", e);
        }
    };

    return (
        <div className="xf__field-row">
            <span className="xf__field-label">{label}</span>

            <span className="xf__field-value">{value}</span>

            {copyable ? (
                <button
                    type="button"
                    className={`xf__field-copy${copied ? " xf__field-copy--done" : ""}`}
                    onClick={handleCopy}
                >
                    {copied ? "Copied ✓" : "Copy"}
                </button>
            ) : (
                <span className={`xf__field-tag xf__field-tag--${tagType}`}>
                    {tag}
                </span>
            )}
        </div>
    );
}
/* ---------------- Step data ---------------- */

const STEPS = [
    {
        n: "01",
        title: "Open the Xero Developer Portal",
        time: "1 min",
        body: (
            <>
                <p>
                    Start by opening the <b>Xero Developer Portal</b>. Sign in using the Xero
                    account that owns the organisation you want to connect to Quick Query, or
                    an account that has the required administrator access.
                </p>

                <CopyBox url="https://developer.xero.com/app/manage" />

                <Shot
                    caption="Xero Developer Portal — My Apps page"
                    img="/step1.jpg"
                />

                <p>
                    After signing in, you will be taken to the <b>My Apps</b> page. This page
                    contains all Xero applications associated with your developer account.
                </p>

                <ul>
                    <li>
                        If you already have a Quick Query app, open that existing app.
                    </li>
                    <li>
                        If you are creating the integration for the first time, continue to{" "}
                        <b>Step 02 — Start a new app</b>.
                    </li>
                </ul>

                <Note>
                    Make sure you are signed in with the correct Xero account. The app and its
                    credentials belong to the Xero developer account where the application is
                    created.
                </Note>
            </>
        ),
    },

    {
        n: "02",
        title: "Create and configure a new app",
        time: "1–2 min",
        body: (
            <>
                <p>
                    From the <b>My Apps</b> page, click <b>New app</b> to start creating the
                    Xero application that Quick Query will use for authentication.
                </p>

                <Shot
                    img="/step2.jpg"
                    caption="My Apps — New app button"
                />

                <p>
                    In the <b>Add a new Xero app</b> dialog, enter the basic application
                    information required by Xero.
                </p>

                <div className="xf__field-table">
                    <FieldRow
                        label="App name"
                        value="QuickQuery"
                        tag="Required"
                        tagType="req"
                    />

                    <FieldRow
                        label="Integration type"
                        value="Web app"
                        tag="Required"
                        tagType="req"
                    />

                    <FieldRow
                        label="Uses Xero data to train an AI model?"
                        value="No"
                        tag="Required"
                        tagType="req"
                    />

                    <FieldRow
                        label="Meets Xero's minimum security requirements?"
                        value="Yes"
                        tag="Required"
                        tagType="req"
                    />

                    <FieldRow
                        label="Company / application URL"
                        value="https://dev.quickquery.com.au/query"
                        tag="Required"
                        tagType="req"
                    />

                    <FieldRow
                        label="OAuth 2.0 redirect URI"
                        value="https://dev.quickquery.com.au/api/v1/organisation/callback"
                        tag="Exact match"
                        tagType="warn"
                        copyable
                    />
                </div>

                <p>
                    Review all the values carefully before creating the application. The
                    <b>OAuth 2.0 redirect URI</b> is especially important because Xero uses it
                    to return the user to Quick Query after authentication.
                </p>

                <Note type="warn">
                    The redirect URI must match Quick Query's callback URL exactly. Do not change
                    the protocol, domain, path, or trailing slash. An incorrect redirect URI can
                    prevent the Xero authentication flow from working.
                </Note>

                <p>
                    Finally, tick{" "}
                    <b>I have read and agree to the Xero Developer Platform Terms &amp; Conditions</b>
                    and click <b>Create app</b>.
                </p>

                <Note>
                    After the app is created successfully, Xero will open the application's
                    details page. From there, continue to the configuration page.
                </Note>
            </>
        ),
    },

    {
        n: "03",
        title: "Open your app's Configuration page",
        time: "30 sec",
        body: (
            <>
                <p>
                    Once your app has been created, open the application from <b>My Apps</b>.
                    From the application's left-hand navigation, select <b>Configuration</b>.
                </p>

                <p>
                    The Configuration page is where you manage the application's OAuth settings,
                    Redirect URIs, Client ID, and Client Secret.
                </p>

                <CopyBox url="https://developer.xero.com/app/manage/app/{your-app-id}/configuration" />

                <Shot
                    img="/step5.jpg"
                    caption="Configuration page — Redirect URIs and Client ID"
                />

                <ul>
                    <li>
                        Confirm that the <b>Redirect URI</b> is the same URL configured for
                        Quick Query.
                    </li>
                    <li>
                        Locate the <b>Client ID</b> section on the Configuration page.
                    </li>
                    <li>
                        The Client ID will be used together with the Client Secret to connect
                        Quick Query to Xero.
                    </li>
                </ul>

                <Note>
                    Keep the Xero Configuration page open. You will need it for the next step
                    to generate and copy the Client Secret.
                </Note>
            </>
        ),
    },

    {
        n: "04",
        title: "Generate the Client Secret",
        time: "1 min",
        body: (
            <>
                <p>
                    On the <b>Configuration</b> page, locate the <b>Client ID</b> and
                    <b>Client Secret</b> sections.
                </p>

                <p>
                    First, make sure you have your <b>Client ID</b>. If it is hidden, click the
                    eye icon to reveal it and use <b>Copy</b> to copy the value.
                </p>

                <div className="xf__field-table">
                    <FieldRow
                        label="Client ID"
                        value="a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                        tag="Public"
                        tagType="public"
                    />
                </div>

                <p>
                    Next, find the <b>Client secrets</b> section and click{" "}
                    <b>Generate a secret</b>. Xero will generate a new Client Secret for your
                    application.
                </p>

                <Shot
                    img="/step4.jpg"
                    caption="Configuration page — Client secrets section"
                    tone="red"
                />

                <div className="xf__field-table">
                    <FieldRow
                        label="Client Secret"
                        value="qq-sk-8Fh2LmQ7tVx3Rn6PbZ0aWs"
                        tag="Secret"
                        tagType="secret"
                    />
                </div>

                <p>
                    Copy the generated Client Secret immediately and store it securely. You will
                    need both the <b>Client ID</b> and <b>Client Secret</b> when configuring the
                    Xero connection in Quick Query.
                </p>

                <Note type="warn">
                    The Client Secret is sensitive information. Do not share it with anyone,
                    include it in screenshots, or commit it to source control. If you lose the
                    secret and it cannot be retrieved, revoke it and generate a new one.
                </Note>

                <Note>
                    The Client ID is a public identifier, while the Client Secret must be kept
                    private.
                </Note>
            </>
        ),
    },

    {
        n: "05",
        title: "Connect Quick Query to Xero",
        time: "1 min",
        body: (
            <>
                <p>
                    Now return to <b>Quick Query</b> and open the Xero integration settings.
                    Go to <b>Settings → Integrations → Xero</b>.
                </p>

                <p>
                    Enter the credentials you copied from the Xero Developer Portal:
                </p>

                <div className="xf__field-table">
                    <FieldRow
                        label="Client ID"
                        value="from Step 04"
                        tag="Public"
                        tagType="public"
                    />

                    <FieldRow
                        label="Client Secret"
                        value="from Step 04"
                        tag="Secret"
                        tagType="secret"
                    />
                </div>

                <p>
                    After entering both values, click <b>Save</b> to store the Xero application
                    credentials.
                </p>

                <p>
                    Once the credentials are saved, click <b>Connect to Xero</b>. Quick Query
                    will redirect you to Xero's authorization page.
                </p>

                <ul>
                    <li>
                        Sign in to Xero if you are not already signed in.
                    </li>
                    <li>
                        Select the Xero organisation you want to connect.
                    </li>
                    <li>
                        Review the requested permissions.
                    </li>
                    <li>
                        Approve the connection to allow Quick Query to access the required
                        Xero data.
                    </li>
                </ul>

                <p>
                    After authorization is completed, Xero will redirect you back to Quick Query.
                    The Xero connection should then appear as connected and ready to use.
                </p>

                <Note type="warn">
                    Treat the Client Secret like a password. Never share it publicly, put it in
                    frontend source code, screenshot it, or commit it to Git/source control.
                </Note>

                <Note>
                    If the connection fails, first verify that the Client ID, Client Secret,
                    and OAuth 2.0 redirect URI are correct and that the redirect URI matches
                    exactly.
                </Note>
            </>
        ),
    },
];


/* ---------------- Main page ---------------- */
export default function XeroCredentialsFull() {
    const [done, setDone] = useState({});
    const doneCount = Object.values(done).filter(Boolean).length;
    const pct = Math.round((doneCount / STEPS.length) * 100);

    const toggle = (n) => setDone((prev) => ({ ...prev, [n]: !prev[n] }));

    return (
        <div className="xf">
            {/* Top progress bar */}


            {/* Hero */}
            <header className="xf__hero">
                <div className="xf__hero-inner">
                    <div className="xf__badge">
                        <IconKey />
                        <span>Quick Query · Xero Integration</span>
                    </div>
                    <h1>
                        Get your Client ID &amp; Client Secret <span className="xf__hero-accent">from Xero</span>
                    </h1>
                    <p className="xf__hero-lede">
                        A clear, step-by-step walkthrough for creating a Xero app and pulling the two
                        credentials Quick Query needs to sync your data securely.
                    </p>

                    <div className="xf__stat-row">
                        <div className="xf__stat">
                            <IconClock />
                            <div>
                                <strong>~7 minutes</strong>
                                <span>Total setup time</span>
                            </div>
                        </div>
                        <div className="xf__stat">
                            <IconShield />
                            <div>
                                <strong>OAuth 2.0</strong>
                                <span>Secure connection</span>
                            </div>
                        </div>
                        <div className="xf__stat">
                            <IconKey />
                            <div>
                                <strong>2 credentials</strong>
                                <span>Client ID + Secret</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main layout: content + sticky side rail, generous side margins */}
            <div className="xf__layout">
                <main className="xf__content">
                    {STEPS.map((step) => {
                        const isDone = !!done[step.n];
                        return (
                            <section key={step.n} id={`step-${step.n}`} className={`xf__step${isDone ? " xf__step--done" : ""}`}>
                                <div className="xf__step-rail">
                                    <span className="xf__step-num">{isDone ? <IconCheck /> : step.n}</span>
                                    <span className="xf__step-line" />
                                </div>
                                <div className="xf__step-card">
                                    <div className="xf__step-headrow">
                                        <div>
                                            <span className="xf__step-time">{step.time}</span>
                                            <h2>{step.title}</h2>
                                        </div>
                                        <button
                                            type="button"
                                            className={`xf__done-btn${isDone ? " xf__done-btn--active" : ""}`}
                                            onClick={() => toggle(step.n)}
                                        >
                                            <IconCheck />
                                            {isDone ? "Done" : "Mark done"}
                                        </button>
                                    </div>
                                    <div className="xf__step-body">{step.body}</div>
                                </div>
                            </section>
                        );
                    })}

                    {doneCount === STEPS.length && (
                        <div className="xf__finish">
                            <span className="xf__finish-icon"><IconSpark /></span>
                            <div>
                                <h3>You're all set</h3>
                                <p>Your Xero Client ID and Client Secret are saved. Head back to Quick Query to see your data sync.</p>
                            </div>
                        </div>
                    )}
                </main>

                {/* Sticky side rail */}
                <aside className="xf__aside">
                    <div className="xf__aside-card">
                        <h3>On this page</h3>
                        <ul>
                            {STEPS.map((s) => (
                                <li key={s.n} className={done[s.n] ? "xf__aside-done" : ""}>
                                    <a href={`#step-${s.n}`}>
                                        <span>{s.n}</span>
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                        <div className="xf__aside-progress">
                            <div className="xf__aside-progress-track">
                                <div className="xf__aside-progress-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <span>{doneCount}/{STEPS.length} steps complete</span>
                        </div>
                    </div>

                    <div className="xf__aside-card xf__aside-card--tip">
                        <IconBulb />
                        <p>
                            Keep the Xero Developer Portal open in one tab and Quick Query settings in another
                            so you can paste credentials as soon as you copy them.
                        </p>
                    </div>
                </aside>
            </div>

            <footer className="xf__footer">
                <span>Need help? Contact your Quick Query administrator or Xero support.</span>
            </footer>
        </div>
    );
}